// ── DOCUMENTS PROTEGES — POINT UNIQUE D'ACCES COTE CLIENT ───────────────────
// Emplacement propose : frontend/src/services/documents.js
//
// ⚠️ LE NOM DE FICHIER EST COMPOSE PAR LE SERVEUR, ET PAR LUI SEUL.
//    Ce module le LIT dans `Content-Disposition`. Il n'en compose aucune part,
//    n'en devine aucune, et n'a AUCUN repli qui fabrique un nom : si l'en-tete
//    est absent ou illisible, on retombe sur l'UUID du document. Un nom
//    fabrique ici serait un second endroit de verite — c'est exactement le
//    defaut qu'on vient de payer sur le logo editeur, ou chaque generateur
//    reimplementait sa part et l'un d'eux avait oublie.
//
// ⚠️ LES ECRANS APPELLENT, ILS N'ASSEMBLENT PAS. Aucun des ecrans consommateurs
//    ne doit contenir de logique de nommage : ils invoquent `apercu(...)` ou
//    `telecharger(...)` avec une URL, rien d'autre.

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * REPLI, DEFINI UNE SEULE FOIS : l'UUID du document tel qu'il figure dans
 * l'URL. Jamais un nom invente, jamais un libelle devine cote client.
 */
function repliUuid(url, typeMime) {
  const m = String(url).match(UUID_RE);
  const ext = typeMime && typeMime.includes('pdf') ? '.pdf' : '';
  return m ? `${m[0]}${ext}` : `document${ext}`;
}

/**
 * Lit le nom d'affichage dans l'en-tete. Deux formes, dans CET ordre :
 *   1. `filename*=UTF-8''…`  (RFC 5987) — porte le nom EXACT, accents compris.
 *   2. `filename="…"`        — repli ASCII pour les vieux clients.
 * ⚠️ La forme etoilee d'ABORD : commencer par `filename=` ramenerait la version
 *    depouillee de ses accents alors que la bonne est juste a cote.
 */
function nomDepuisEnTete(enTete) {
  if (!enTete) return null;

  const etoile = enTete.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (etoile) {
    try {
      const decode = decodeURIComponent(etoile[1].trim());
      if (decode) return decode;
    } catch (_) {
      // Percent-encoding illisible : on ne devine pas, on passe a la forme simple.
    }
  }

  const simple = enTete.match(/filename\s*=\s*"([^"]+)"/i) || enTete.match(/filename\s*=\s*([^;]+)/i);
  if (simple) {
    const v = simple[1].trim();
    if (v) return v;
  }
  return null;
}

/**
 * Recupere un document protege. Rend le blob ET le nom compose par le serveur.
 * @returns {Promise<{ blob: Blob, nom: string, type: string }>}
 */
export async function recupererDocument(url) {
  const jeton = localStorage.getItem('gss_token');
  const res = await fetch(url, { headers: jeton ? { Authorization: `Bearer ${jeton}` } : {} });
  if (!res.ok) throw new Error(`Erreur ${res.status}`);

  const type = res.headers.get('Content-Type') || 'application/octet-stream';
  const nom = nomDepuisEnTete(res.headers.get('Content-Disposition')) || repliUuid(url, type);
  return { blob: await res.blob(), nom, type };
}

/**
 * APERCU — ouvre le document dans un onglet.
 *
 * ⚠️ LIMITE : `apercuDocument` ouvre une `blob:` URL, depourvue d'en-tete, donc
 *    « Enregistrer sous » depuis la visionneuse propose toujours l'identifiant
 *    du blob. NON CONTOURNABLE COTE RENDU — aucune API ne permet de nommer une
 *    blob URL, et `new File([blob], nom)` ne change pas l'URL produite.
 *
 *    RESOLUTION POSSIBLE, CHANTIER SEPARE : servir le document via une URL
 *    SIGNEE A DUREE COURTE, ouvrable directement dans un onglet sans jeton
 *    d'en-tete. Le visualiseur natif lirait alors le `Content-Disposition` et
 *    proposerait le bon nom, SANS second bouton. Touche l'authentification —
 *    a instruire avec le chantier d'isolation, PAS ici.
 *
 *    En attendant, le nom compose s'obtient par `telechargerDocument`.
 *
 * ⚠️ SECOND DEFAUT, HERITE ET NON INTRODUIT ICI : `window.open` est appele
 *    APRES un `await`, donc hors du « user gesture » du clic — Firefox le bloque
 *    comme une pop-up, sans erreur visible. C'est deja le comportement des 8
 *    ecrans aujourd'hui (tous font `await fetchFile(...)` puis `window.open`),
 *    et c'est precisement la raison d'etre de `downloadPdfBlob` (voir son
 *    en-tete). Le telechargement, lui, passe par une ancre `<a download>` et
 *    n'est JAMAIS bloque. Une raison de plus pour la piste « URL signee ».
 */
export async function apercuDocument(url) {
  const { blob, type } = await recupererDocument(url);
  const objet = URL.createObjectURL(new Blob([blob], { type }));
  window.open(objet, '_blank');
  setTimeout(() => URL.revokeObjectURL(objet), 60000);
}

/**
 * TELECHARGEMENT — enregistre le document sous le nom compose par le SERVEUR.
 * C'est le seul chemin qui livre le bon nom dans « Enregistrer sous ».
 */
export async function telechargerDocument(url) {
  const { blob, nom, type } = await recupererDocument(url);
  const objet = URL.createObjectURL(new Blob([blob], { type }));
  const a = document.createElement('a');
  a.href = objet;
  a.download = nom;                     // <- le nom vient du serveur, jamais d'ici
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke immediat peut annuler le telechargement sur certains navigateurs.
  setTimeout(() => URL.revokeObjectURL(objet), 10000);
  return nom;
}

// Exporte aussi les lecteurs pour qu'ils soient TESTABLES isolement — et pour
// qu'aucun ecran n'ait de raison d'en reecrire un.
export const _internes = { nomDepuisEnTete, repliUuid };
